/**
 * Project model and its compilation into an evaluable form.
 *
 * A project is a flat set of variables plus a small dependency graph of named
 * intermediates and outputs. Compilation resolves that graph once: it checks
 * for undefined references and cycles, orders the intermediates, assigns dense
 * environment slots, and produces both a fast closure per output and a fully
 * inlined AST per output for the differentiation-based methods.
 */

import type { Distribution } from './distributions.ts';
import { validate as validateDistribution, nominal, bounds } from './distributions.ts';
import {
  prepareCorrelation,
  type CorrelationPair,
  type CorrelationStructure,
} from './correlation.ts';
export type { CorrelationPair } from './correlation.ts';
import { parse } from './expr/parse.ts';
import { compile, type Compiled } from './expr/compile.ts';
import { type Node, referencedNames, isSmooth } from './expr/ast.ts';

/**
 * One varying quantity, drawn exactly once per trial and read by every
 * expression that names it.
 *
 * There is no per-instance replication anywhere in the model, which is what
 * satisfies PRD §4.2: an environmental quantity shared by the whole assembly
 * is declared once and referenced from as many expressions as you like, and
 * three resistors are three variables. (A `scope: 'component' | 'global'` field
 * existed up to v1.2 to label which was which. No engine code ever read it, so
 * it looked like a sampling mode without being one; it was removed rather than
 * documented. Files that still carry the key are accepted and ignore it.)
 */
export interface Variable {
  /** Stable identifier, referenced verbatim inside expressions. */
  id: string;
  name?: string;
  unit?: string;
  distribution: Distribution;
  /** Correlation group membership (PRD §4.3). */
  group?: string;
}

export interface CorrelationGroup {
  id: string;
  /**
   * Common-mode fraction in [0,1]. Each member is drawn as a rho-weighted blend
   * of one shared group draw and one independent draw, giving pairwise
   * correlation rho between members. rho=1 is a perfectly matched set.
   */
  rho: number;
  label?: string;
}

export interface Intermediate {
  id: string;
  expr: string;
  unit?: string;
}

export interface OutputSpec {
  id: string;
  expr: string;
  unit?: string;
  lsl?: number;
  usl?: number;
  target?: number;
}

export interface Project {
  schemaVersion: 1;
  name: string;
  /** Stored in the file and printed in every report (PRD §4.8, §6.2). */
  seed: number;
  variables: Variable[];
  groups?: CorrelationGroup[];
  /**
   * Explicit pairwise correlations (PRD §4.3, advanced model). A variable may
   * be in a group or in this matrix, but not both — the two mechanisms would
   * otherwise fight over the same underlying normal.
   */
  correlations?: CorrelationPair[];
  /** Use the nearest valid matrix when the supplied one is not positive semi-definite. */
  repairCorrelation?: boolean;
  intermediates?: Intermediate[];
  outputs: OutputSpec[];
}

export class ModelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ModelError';
  }
}

export interface CompiledOutput {
  spec: OutputSpec;
  /** Reads the environment vector; intermediates must already be filled. */
  fn: Compiled;
  /** Intermediates substituted through, so it depends only on variables. */
  inlined: Node;
  /** False when the expression contains abs/min/max/if/rounding (PRD §4.5). */
  smooth: boolean;
}

export interface CompiledModel {
  project: Project;
  /** Environment indices [0, varIds.length) — one slot per variable. */
  varIds: string[];
  varIndex: ReadonlyMap<string, number>;
  distributions: Distribution[];
  /** Group index per variable, or -1. Parallel to `varIds`. */
  varGroup: Int32Array;
  groups: CorrelationGroup[];
  /** Explicit correlation structure, or null when none is configured. */
  correlation: CorrelationStructure | null;
  /** Matrix row per variable, or -1. Parallel to `varIds`. */
  varCorrRow: Int32Array;
  /** Variable index per matrix row. */
  corrRowVar: Int32Array;
  envSize: number;
  outputs: CompiledOutput[];
  /** Fill the intermediate slots of `env` from its variable slots. */
  fillIntermediates: (env: Float64Array) => void;
  /** Convenience: evaluate every output into `dst`. */
  evaluate: (env: Float64Array, dst: Float64Array) => Float64Array;
  /** Environment vector with every variable at its nominal value. */
  nominalEnv: () => Float64Array;
}

/** Guard against pathological blowup when inlining deeply reused intermediates. */
const MAX_INLINED_NODES = 200_000;

function countNodes(node: Node): number {
  switch (node.kind) {
    case 'num':
    case 'ref':
      return 1;
    case 'neg':
      return 1 + countNodes(node.operand);
    case 'binary':
    case 'compare':
      return 1 + countNodes(node.left) + countNodes(node.right);
    case 'call':
      return 1 + node.args.reduce((a, x) => a + countNodes(x), 0);
    case 'if':
      return 1 + countNodes(node.cond) + countNodes(node.then) + countNodes(node.otherwise);
  }
}

/** Substitute intermediate definitions until only variable refs remain. */
function inline(node: Node, defs: ReadonlyMap<string, Node>): Node {
  switch (node.kind) {
    case 'num':
      return node;
    case 'ref': {
      const def = defs.get(node.name);
      return def ? inline(def, defs) : node;
    }
    case 'neg':
      return { kind: 'neg', operand: inline(node.operand, defs) };
    case 'binary':
      return {
        kind: 'binary',
        op: node.op,
        left: inline(node.left, defs),
        right: inline(node.right, defs),
      };
    case 'compare':
      return {
        kind: 'compare',
        op: node.op,
        left: inline(node.left, defs),
        right: inline(node.right, defs),
      };
    case 'call':
      return { kind: 'call', name: node.name, args: node.args.map((a) => inline(a, defs)) };
    case 'if':
      return {
        kind: 'if',
        cond: inline(node.cond, defs),
        then: inline(node.then, defs),
        otherwise: inline(node.otherwise, defs),
      };
  }
}

export function buildModel(project: Project): CompiledModel {
  if (project.variables.length === 0) {
    throw new ModelError('Project has no variables');
  }
  if (project.outputs.length === 0) {
    throw new ModelError('Project has no outputs');
  }

  // --- Variables -> dense slots ---------------------------------------------
  const varIds: string[] = [];
  const varIndex = new Map<string, number>();
  const distributions: Distribution[] = [];

  for (const v of project.variables) {
    if (varIndex.has(v.id)) throw new ModelError(`Duplicate variable id '${v.id}'`);
    validateDistribution(v.distribution, `variable '${v.id}'`);
    varIndex.set(v.id, varIds.length);
    varIds.push(v.id);
    distributions.push(v.distribution);
  }

  // --- Correlation groups ----------------------------------------------------
  const groups = project.groups ?? [];
  const groupIndex = new Map<string, number>();
  groups.forEach((g, i) => {
    if (groupIndex.has(g.id)) throw new ModelError(`Duplicate group id '${g.id}'`);
    if (!(g.rho >= 0 && g.rho <= 1)) {
      throw new ModelError(`Group '${g.id}': rho must lie in [0, 1], got ${g.rho}`);
    }
    groupIndex.set(g.id, i);
  });

  const varGroup = new Int32Array(varIds.length).fill(-1);
  project.variables.forEach((v, i) => {
    if (v.group === undefined) return;
    const gi = groupIndex.get(v.group);
    if (gi === undefined) {
      throw new ModelError(`Variable '${v.id}' references unknown group '${v.group}'`);
    }
    varGroup[i] = gi;
  });

  // --- Explicit correlation matrix -------------------------------------------
  const pairs = project.correlations ?? [];
  for (const p of pairs) {
    for (const name of [p.a, p.b]) {
      if (!varIndex.has(name)) {
        throw new ModelError(`Correlation references unknown variable '${name}'`);
      }
    }
    // Both mechanisms drive the same underlying normal, so allowing a variable
    // in both would make the effective correlation depend on evaluation order.
    for (const name of [p.a, p.b]) {
      const v = project.variables[varIndex.get(name)!];
      if (v.group !== undefined) {
        throw new ModelError(
          `Variable '${name}' is in correlation group '${v.group}' and also has an explicit ` +
            'correlation. Use one mechanism or the other for a given variable.',
        );
      }
    }
  }

  let correlation: CorrelationStructure | null = null;
  try {
    correlation = prepareCorrelation(pairs, project.repairCorrelation ?? false);
  } catch (e) {
    throw new ModelError((e as Error).message);
  }

  const varCorrRow = new Int32Array(varIds.length).fill(-1);
  const corrRowVar = new Int32Array(correlation ? correlation.ids.length : 0);
  if (correlation) {
    correlation.ids.forEach((id, row) => {
      const vi = varIndex.get(id)!;
      varCorrRow[vi] = row;
      corrRowVar[row] = vi;
    });
  }

  // --- Intermediates: parse, check references, topologically order -----------
  const intermediates = project.intermediates ?? [];
  const interAst = new Map<string, Node>();

  for (const m of intermediates) {
    if (varIndex.has(m.id)) {
      throw new ModelError(`Intermediate '${m.id}' collides with a variable of the same id`);
    }
    if (interAst.has(m.id)) throw new ModelError(`Duplicate intermediate id '${m.id}'`);
    try {
      interAst.set(m.id, parse(m.expr));
    } catch (e) {
      throw new ModelError(`Intermediate '${m.id}': ${(e as Error).message}`);
    }
  }

  const knownName = (n: string) => varIndex.has(n) || interAst.has(n);
  for (const [id, ast] of interAst) {
    for (const ref of referencedNames(ast)) {
      if (!knownName(ref)) {
        throw new ModelError(`Intermediate '${id}' references unknown name '${ref}'`);
      }
    }
  }

  // Depth-first topological sort with cycle detection.
  const order: string[] = [];
  const state = new Map<string, 'visiting' | 'done'>();
  const visit = (id: string, path: string[]): void => {
    const s = state.get(id);
    if (s === 'done') return;
    if (s === 'visiting') {
      const cycle = [...path.slice(path.indexOf(id)), id].join(' -> ');
      throw new ModelError(`Circular reference: ${cycle}`);
    }
    state.set(id, 'visiting');
    for (const ref of referencedNames(interAst.get(id)!)) {
      if (interAst.has(ref)) visit(ref, [...path, id]);
    }
    state.set(id, 'done');
    order.push(id);
  };
  for (const id of interAst.keys()) visit(id, []);

  // --- Environment layout: variables first, then intermediates in dep order --
  const slots = new Map<string, number>(varIndex);
  order.forEach((id) => slots.set(id, slots.size));
  const envSize = slots.size;

  const interSteps = order.map((id) => ({
    index: slots.get(id)!,
    fn: compile(interAst.get(id)!, slots),
  }));

  const fillIntermediates = (env: Float64Array): void => {
    for (let i = 0; i < interSteps.length; i++) {
      env[interSteps[i].index] = interSteps[i].fn(env);
    }
  };

  // --- Outputs ---------------------------------------------------------------
  const seenOutput = new Set<string>();
  const outputs: CompiledOutput[] = project.outputs.map((spec) => {
    if (seenOutput.has(spec.id)) throw new ModelError(`Duplicate output id '${spec.id}'`);
    seenOutput.add(spec.id);

    let ast: Node;
    try {
      ast = parse(spec.expr);
    } catch (e) {
      throw new ModelError(`Output '${spec.id}': ${(e as Error).message}`);
    }
    for (const ref of referencedNames(ast)) {
      if (!knownName(ref)) {
        throw new ModelError(`Output '${spec.id}' references unknown name '${ref}'`);
      }
    }
    if (spec.lsl !== undefined && spec.usl !== undefined && spec.lsl >= spec.usl) {
      throw new ModelError(`Output '${spec.id}': LSL must be < USL`);
    }

    const inlined = inline(ast, interAst);
    if (countNodes(inlined) > MAX_INLINED_NODES) {
      throw new ModelError(
        `Output '${spec.id}' expands to an unmanageable expression; ` +
          `reduce reuse of intermediates or split the model`,
      );
    }

    return { spec, fn: compile(ast, slots), inlined, smooth: isSmooth(inlined) };
  });

  const evaluate = (env: Float64Array, dst: Float64Array): Float64Array => {
    fillIntermediates(env);
    for (let i = 0; i < outputs.length; i++) dst[i] = outputs[i].fn(env);
    return dst;
  };

  const nominalEnv = (): Float64Array => {
    const env = new Float64Array(envSize);
    for (let i = 0; i < distributions.length; i++) env[i] = nominal(distributions[i]);
    fillIntermediates(env);
    return env;
  };

  return {
    project,
    varIds,
    varIndex,
    distributions,
    varGroup,
    groups,
    correlation,
    varCorrRow,
    corrRowVar,
    envSize,
    outputs,
    fillIntermediates,
    evaluate,
    nominalEnv,
  };
}

/** Per-variable interval used as the WCA box, with the sigma clamp recorded. */
export function variableBounds(
  model: CompiledModel,
  sigmaClamp?: number,
): { min: Float64Array; max: Float64Array; clamped: boolean[] } {
  const n = model.varIds.length;
  const min = new Float64Array(n);
  const max = new Float64Array(n);
  const clamped: boolean[] = [];
  for (let i = 0; i < n; i++) {
    const b = bounds(model.distributions[i], sigmaClamp);
    min[i] = b.min;
    max[i] = b.max;
    clamped.push(b.clamped);
  }
  return { min, max, clamped };
}
